<script setup>
/* global axios */
// Ítem del menú lateral que abre el panel del ISP (ctrl/WHMCS) en una pestaña nueva.
// El enlace lo firma el backend (custom/deferred/hybschat_dash_sso_controller.rb): el
// secreto compartido nunca llega al bundle. ctrl valida la firma y comprueba que el
// usuario sea el titular del tenant; a los demás administradores les muestra su propia
// página explicándolo.
// Archivo NUEVO sin rastrear: sobrevive los upgrades. El único parche es su import y
// su render en Sidebar.vue.
import { ref, computed } from 'vue';
import { useSidebarContext } from './provider';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Icon from 'next/icon/Icon.vue';

// Los ítems nativos del menú no llevan ningún adorno de "enlace externo" — es el
// único icono de ese tipo que habría en toda la barra — así que el aviso de que
// abre una pestaña va en el title, y el estado de carga reemplaza el icono.
const LABEL = 'Gerencia';
const TITLE = `${LABEL} — se abre en una pestaña nueva`;
const ICON = 'i-lucide-layout-dashboard';
const ERROR_MESSAGE =
  'No pudimos abrir el panel. Intentá de nuevo en un momento.';

const { isCollapsed } = useSidebarContext();
const { accountId } = useAccount();
const currentRole = useMapGetter('getCurrentRole');

const isOpening = ref(false);
const isAdministrator = computed(() => currentRole.value === 'administrator');
const icon = computed(() =>
  isOpening.value ? 'i-lucide-loader-circle' : ICON
);

const openPanel = async () => {
  if (isOpening.value) return;
  isOpening.value = true;

  // La pestaña se abre de forma síncrona con el clic, si no el navegador la bloquea.
  // El enlace se firma recién ahí porque vence a los 5 minutos.
  const tab = window.open('about:blank', '_blank');
  if (tab) tab.opener = null;

  try {
    const { data } = await axios.get(
      `/api/v1/accounts/${accountId.value}/hybschat/dash_sso_url`
    );
    if (tab) {
      tab.location.href = data.url;
    } else {
      window.location.href = data.url;
    }
  } catch {
    if (tab) tab.close();
    useAlert(ERROR_MESSAGE);
  } finally {
    isOpening.value = false;
  }
};
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <li
    v-if="isAdministrator"
    class="grid gap-1 text-sm cursor-pointer select-none min-w-0"
  >
    <button
      v-if="isCollapsed"
      type="button"
      class="flex items-center justify-center size-10 rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
      :title="TITLE"
      @click="openPanel"
    >
      <Icon
        :icon="icon"
        class="size-4"
        :class="{ 'animate-spin': isOpening }"
      />
    </button>
    <button
      v-else
      type="button"
      class="flex items-center gap-2 px-1.5 py-1 rounded-lg h-8 min-w-0 w-full text-n-slate-11 hover:bg-n-alpha-2"
      :title="TITLE"
      @click="openPanel"
    >
      <div class="relative flex items-center gap-2">
        <Icon
          :icon="icon"
          class="size-4"
          :class="{ 'animate-spin': isOpening }"
        />
      </div>
      <div
        class="flex items-center gap-1.5 flex-grow justify-between min-w-0 flex-1"
      >
        <span class="truncate text-body-main text-start">{{ LABEL }}</span>
      </div>
    </button>
  </li>
</template>
